import { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { FilterInput } from '../shared/FilterInput';
import AppToast from '../../../../shared/components/AppToast';
import { ensureCatalogAuth } from '../../../../services/backend/authService';
import { getProductosByCriteria, asociarProducto } from '../../../../services/backend/productoService';
import logger from '../../../../shared/utils/logger';

export const AsociarProductosDialog = ({ visible, onHide, familia, tipo, productosAsociados = [], onAsociado }) => {
    const toastRef = useRef(null);
    const [allProducts, setAllProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);

    const familiaId = familia?.id || 0;
    const familiaName = familia?.name || familia?.nombre || '';

    useEffect(() => {
        if (visible) {
            let mounted = true;
            const load = async () => {
                setLoading(true);
                try {
                    await ensureCatalogAuth();
                    const response = await getProductosByCriteria({
                        tipo: tipo,
                        familia: 0,
                        descripcion: '',
                        start: 0,
                        length: 9999,
                    });
                    const items = Array.isArray(response?.productos) ? response.productos : Array.isArray(response) ? response : [];
                    if (mounted) setAllProducts(items);
                } catch (err) {
                    logger.error("Error al cargar productos disponibles:", err);
                    if (mounted) setAllProducts([]);
                } finally {
                    if (mounted) setLoading(false);
                }
            };
            load();
            setSelectedProducts([]);
            return () => { mounted = false; };
        }
    }, [tipo, visible]);

    const codigosAsociados = new Set(productosAsociados.map((p) => p.codigo));
    const availableProducts = allProducts.filter((p) => !codigosAsociados.has(p.codigo));

    const handleAsociar = async () => {
        if (!selectedProducts.length || !familiaId) return;
        setLoading(true);
        const conflictos = [];
        let fatalError = null;
        try {
            await ensureCatalogAuth();
            for (const prod of selectedProducts) {
                try {
                    await asociarProducto({
                        codigo: prod.codigo,
                        familia: familiaId,
                        tipo: tipo,
                    });
                } catch (err) {
                    if (err?.conflict || err?.status === 409) {
                        conflictos.push({
                            codigo: prod.codigo,
                            mensaje: err?.body?.mensaje || err?.message || `El producto ya tiene familia del canal ${tipo}.`,
                        });
                    } else {
                        fatalError = err;
                        break;
                    }
                }
            }
            if (fatalError) throw fatalError;

            const okCount = selectedProducts.length - conflictos.length;
            if (okCount > 0) {
                toastRef.current?.show({ severity: 'success', summary: 'Productos asociados', detail: `${okCount} producto(s) asociado(s) a "${familiaName}".`, life: 3000, });
            }
            if (conflictos.length) {
                const lista = conflictos.map((c) => c.codigo).join(', ');
                toastRef.current?.show({
                    severity: 'warn',
                    summary: 'Productos ya asociados',
                    detail: conflictos.length === 1
                        ? conflictos[0].mensaje
                        : `Ya asociados a otra familia del canal ${tipo}: ${lista}.`,
                    life: 5000,
                });
            }
            setSelectedProducts([]);
            onAsociado?.();
            onHide();
        } catch (err) {
            logger.error("Error al asociar productos:", err);
            toastRef.current?.show({ severity: 'error', summary: 'Error', detail: err?.body?.mensaje || err?.message || 'No se pudieron asociar los productos.', life: 4000 });
        } finally {
            setLoading(false);
        }
    };

    const header = (
        <div className="flex align-items-center justify-content-between flex-wrap gap-2">
            <span className="text-sm font-semibold">
                Productos disponibles ({tipo}) — Asociar a: {familiaName}
            </span>
            <Button
                className="p-button-rounded p-button-sm"
                icon="pi pi-link"
                label="Asociar"
                onClick={handleAsociar}
                disabled={!selectedProducts.length}
                severity="success"
            />
        </div>
    );

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button className="p-button-text" label="Cerrar" onClick={onHide} />
        </div>
    );

    return (
        <>
            <AppToast ref={toastRef} />
            <Dialog
            header="Asociar Productos"
            visible={visible}
            onHide={onHide}
            footer={footer}
            style={{ width: '80vw' }}
            maximizable
        >
            <DataTable
                value={availableProducts}
                header={header}
                showGridlines
                paginator
                rows={10}
                dataKey="codigo"
                stripedRows
                filterDisplay="row"
                filter
                scrollable
                scrollHeight="50vh"
                loading={loading}
                rowsPerPageOptions={[5, 10, 25, 50]}
                tableStyle={{ minWidth: '50rem' }}
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} productos"
                selection={selectedProducts}
                onSelectionChange={(e) => setSelectedProducts(e.value)}
                selectionMode="multiple"
            >
                <Column selectionMode="multiple" headerStyle={{ width: '5%' }} />
                <Column field="codigo" header="Código" sortable style={{ width: "15%" }} filter filterElement={FilterInput} />
                <Column field="descripcion" header="Descripción" sortable style={{ width: "50%" }} filter filterElement={FilterInput} />
            </DataTable>
        </Dialog>
        </>
    );
};
